import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  Avatar,
  Chip,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip
} from '@mui/material';
import {
  Search,
  ArrowBack,
  FilterList,
  WorkspacePremium,
  EventNoteOutlined,
  AttachMoneyOutlined,
  StorefrontOutlined
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { getClientDirectoryCrm } from '../../../controllers/company/crm/CrmController';



export default function ClientDirectoryView() {
    const history = useHistory();

    // --- ESTADOS DEL COMPONENTE ---
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Estados de filtros y paginación
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [segment, setSegment] = useState('');
    const [sortBy, setSortBy] = useState('total_spent');
    const [sortDir, setSortDir] = useState('desc');
    
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(15);
    const [totalRows, setTotalRows] = useState(0);

    // --- DEBOUNCE PARA EL BUSCADOR ---
    // Evita llamar a la API por cada letra que el usuario escribe. Espera 500ms.
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
            setPage(1); // Si busca algo nuevo, regresamos a la página 1
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    // --- LLAMADA A LA API ---
    useEffect(() => {
        let isMounted = true;

        const fetchClients = async () => {
            setLoading(true);
            const response = await getClientDirectoryCrm({
                page,
                per_page: perPage,
                search: debouncedSearch,
                segment,
                sort_by: sortBy,
                sort_dir: sortDir
            });

			console.log(response)

            if (isMounted) {
                setClients(response.data);
                setTotalRows(response.pagination.total);
                setLoading(false);
            }
        };

        fetchClients();

        return () => { isMounted = false; };
    }, [page, perPage, debouncedSearch, segment, sortBy, sortDir]);

    // --- MANEJADORES DE EVENTOS ---
    const handleSort = (column) => {
        const isAsc = sortBy === column && sortDir === 'asc';
        setSortDir(isAsc ? 'desc' : 'asc');
        setSortBy(column);
        setPage(1);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage + 1); // MUI TablePagination es 0-indexed, nuestra API es 1-indexed
    };

    const handleChangeRowsPerPage = (event) => {
        setPerPage(parseInt(event.target.value, 10));
        setPage(1);
    };

    // --- FUNCIONES AUXILIARES DE UI ---
    const getTierConfig = (spent) => {
        if (spent >= 1000) return { label: 'Diamante', color: '#be185d', bg: '#fdf2f8' };
        if (spent >= 500) return { label: 'Oro', color: '#b45309', bg: '#fef3c7' };
        return { label: 'Plata', color: '#475569', bg: '#f1f5f9' };
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Sin visitas recientes';
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('es-VE', { 
            day: '2-digit', month: 'short', year: 'numeric' 
        }).format(date);
    };

    return (
        <Box sx={{ minHeight: '100vh', py: 4}}>
            <Container maxWidth="xl">
                
                {/* CABECERA Y NAVEGACIÓN */}
                <Stack direction="row" alignItems="center" spacing={2} mb={4}>
                    <IconButton onClick={() => history.push('/crm/dashboard')} sx={{ bgcolor: 'white', border: '1px solid #e2e8f0' }}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight="800" color="text.primary" letterSpacing="-0.5px">
                            Directorio de Clientes
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Gestiona y analiza el comportamiento de toda tu base de usuarios.
                        </Typography>
                    </Box>
                </Stack>

                <Card variant="outlined" sx={{ borderRadius: 4, border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)' }}>
                    
                    {/* BARRA DE HERRAMIENTAS (Filtros y Búsqueda) */}
                    <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', bgcolor: 'white' }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} justifyContent="space-between">
                            
                            <TextField
                                placeholder="Buscar por nombre, correo o teléfono..."
                                variant="outlined"
                                size="small"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                sx={{ minWidth: { xs: '100%', md: '400px' } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search color="action" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: 2, bgcolor: '#f8fafc' }
                                }}
                            />

                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="segment-label">Segmento Rápido</InputLabel>
                                <Select
                                    labelId="segment-label"
                                    value={segment}
                                    label="Segmento Rápido"
                                    onChange={(e) => { setSegment(e.target.value); setPage(1); }}
                                    sx={{ borderRadius: 2, bgcolor: '#f8fafc' }}
                                >
                                    <MenuItem value=""><em>Todos los clientes</em></MenuItem>
                                    <MenuItem value="vip">⭐ Clientes VIP (+$500)</MenuItem>
                                    <MenuItem value="recurrent">🔄 Recurrentes (+2 visitas)</MenuItem>
                                    <MenuItem value="new">🆕 Nuevos (Últimos 30 días)</MenuItem>
                                    <MenuItem value="risk">⚠️ En Riesgo (+60 días sin venir)</MenuItem>
                                </Select>
                            </FormControl>

                        </Stack>
                    </Box>

                    {/* TABLA DE DATOS */}
                    <TableContainer sx={{ minHeight: 400, bgcolor: 'white' }}>
                        {loading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                                <CircularProgress />
                            </Box>
                        ) : clients.length === 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                                <StorefrontOutlined sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">No se encontraron clientes</Typography>
                                <Typography variant="body2" color="text.disabled">Intenta ajustar los filtros de búsqueda.</Typography>
                            </Box>
                        ) : (
                            <Table>
                                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Cliente</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Nivel</TableCell>
                                        
                                        {/* Cabeceras Ordenables */}
                                        <TableCell 
                                            align="right" 
                                            sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                            onClick={() => handleSort('total_spent')}
                                        >
                                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                                <span>Total Gastado</span>
                                                {sortBy === 'total_spent' && <FilterList sx={{ fontSize: 16, transform: sortDir === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)' }} />}
                                            </Stack>
                                        </TableCell>

                                        <TableCell 
                                            align="center" 
                                            sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                            onClick={() => handleSort('total_visits')}
                                        >
                                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5}>
                                                <span>Visitas</span>
                                                {sortBy === 'total_visits' && <FilterList sx={{ fontSize: 16, transform: sortDir === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)' }} />}
                                            </Stack>
                                        </TableCell>

                                        <TableCell 
                                            align="right" 
                                            sx={{ fontWeight: 600, cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
                                            onClick={() => handleSort('last_interaction_at')}
                                        >
                                            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
                                                <span>Última Visita</span>
                                                {sortBy === 'last_interaction_at' && <FilterList sx={{ fontSize: 16, transform: sortDir === 'desc' ? 'rotate(0deg)' : 'rotate(180deg)' }} />}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {clients.map((client) => {
                                        const customerName = client.customer?.name || client.customer?.nombre || 'Cliente Anónimo';
                                        const tier = getTierConfig(client.total_spent);

                                        return (
                                            <TableRow 
												hover 
												key={client.id} 
												sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
												onClick={() => history.push(`/crm/client-detail/${client.customer_id}`)}
											>
                                                
                                                {/* Columna: Perfil */}
                                                <TableCell>
                                                    <Stack direction="row" spacing={2} alignItems="center">
                                                        <Avatar sx={{ bgcolor: 'primary.main', fontWeight: 'bold' }}>
                                                            {customerName.charAt(0).toUpperCase()}
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight="bold" color="text.primary">
                                                                {customerName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {client.customer?.email || 'Sin correo registrado'}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>

                                                {/* Columna: Tier / Nivel */}
                                                <TableCell>
                                                    <Chip 
                                                        label={`Nivel ${tier.label}`}
                                                        size="small"
                                                        icon={<WorkspacePremium fontSize="small" style={{ color: tier.color }} />}
                                                        sx={{ bgcolor: tier.bg, color: tier.color, fontWeight: 600 }}
                                                    />
                                                </TableCell>

                                                {/* Columna: Gastado */}
                                                <TableCell align="right">
                                                    <Typography variant="subtitle2" fontWeight="bold" color="success.main">
                                                        ${parseFloat(client.total_spent).toFixed(2)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Columna: Visitas */}
                                                <TableCell align="center">
                                                    <Chip label={client.total_visits} size="small" variant="outlined" sx={{ fontWeight: 'bold' }} />
                                                </TableCell>

                                                {/* Columna: Última Interacción */}
                                                <TableCell align="right">
                                                    <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                                                        <EventNoteOutlined fontSize="small" color="action" />
                                                        <Typography variant="body2" color="text.secondary" fontWeight="500">
                                                            {formatDate(client.last_interaction_at)}
                                                        </Typography>
                                                    </Stack>
                                                </TableCell>

                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </TableContainer>

                    {/* PAGINACIÓN */}
                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page - 1} // MUI necesita que sea 0-indexed
                        onPageChange={handleChangePage}
                        rowsPerPage={perPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[15, 30, 50, 100]}
                        labelRowsPerPage="Clientes por página:"
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
                        sx={{ bgcolor: 'white', borderTop: '1px solid #e2e8f0' }}
                    />
                </Card>
            </Container>
        </Box>
    );
}